import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { Subscription } from 'rxjs';
import { StorageService } from '../../services/storage.service';
import { HttpService } from '../../services/http.service';
import { TravelCountry, UserAccountDTO } from '../../classes/user-account.dto';
import { CommonModule } from '@angular/common';
import { DataService } from '../../services/data.service';
import { AppointmentStepperService } from '../../services/appointment-stepper.service';
import { ToastrService } from 'ngx-toastr';
import { NzRadioGroupAccessibilityDirective } from '../../directives/nz-radio-group-accessibility.directive';

@Component({
    selector: 'app-assistance-form',
    templateUrl: './assistance-form.component.html',
    styleUrls: ['./assistance-form.component.scss'],
    imports: [CommonModule, TranslateModule, ReactiveFormsModule, NzRadioModule, NzRadioGroupAccessibilityDirective, NzDatePickerModule, NzGridModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AssistanceFormComponent implements OnInit, OnDestroy {
  @Output() assistanceFormEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Input() formType?: 'covid' | 'general';
  @Input() isTravel = false;
  formAssistance?: FormGroup;
  subscription: Array<Subscription | undefined> = [];
  codeCountry: string[] = [];
  descriptionCountry: string[] = [];
  accountData: any;
  travelNation?: TravelCountry;
  companyId?: string;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private storage: StorageService,
    private http: HttpService,
    private dataService: DataService,
    private appointmentStepperService: AppointmentStepperService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    if (this.route.snapshot.data['type']) {
      this.formType = this.route.snapshot.data['type'];
    }
    if (this.isTravel) {
      this.formAssistance = this.fb.group({});
    } else {
      this.formAssistance = this.fb.group({
        telephone: [userData.phoneNumber || null, Validators.required],
        message: [null, Validators.required],
      });
    }
    if (this.formType === 'general') {
      this.formAssistance.addControl('travel', new FormControl(false, Validators.required));
      this.handleFormFields();
      this.http.getTravelCountries('it').subscribe((countries) => {
        this.codeCountry = countries.map((country) => country.code);
        this.descriptionCountry = countries.map((country) => country.description);
      });
      this.http.getAccountdDataNoConnectionValidations(individualId).subscribe((response) => {
        this.accountData = response;
      });
    }
    const oldTravelData = this.appointmentStepperService.getTravelFormData()?.form;
    if (oldTravelData) {
      this.formAssistance?.get('travel')?.patchValue(oldTravelData.value.travel);
      this.formAssistance.get('destination')?.patchValue(oldTravelData.value.destination);
      this.formAssistance.get('departure')?.patchValue(oldTravelData.value.departure);
      this.formAssistance.get('return')?.patchValue(oldTravelData.value.return);
    }
    this.checkValidationForm();
    this.assistanceFormEmitter.emit({ form: this.formAssistance, destination: this.travelNation?.description });
    this.emitValueForm();
  }

  private checkValidationForm(): void {
    this.subscription.push(
      this.formAssistance?.valueChanges.subscribe(() => {
        this.cdr.detectChanges();
      })
    );
  }

  private handleFormFields(): void {
    if (this.formAssistance && this.formAssistance?.get('travel') && this.formType === 'general') {
      this.subscription.push(
        this.formAssistance.get('travel')?.valueChanges.subscribe((value: boolean) => {
          if (value) {
            this.formAssistance?.addControl('destination', new FormControl(null, Validators.required));
            this.formAssistance?.addControl('departure', new FormControl(null, Validators.required));
            this.formAssistance?.addControl('return', new FormControl(null, Validators.required));
          } else {
            this.formAssistance?.removeControl('destination');
            this.formAssistance?.removeControl('departure');
            this.formAssistance?.removeControl('return');
          }
        })
      );
    }
  }

  sendRequest(): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const message = this.formAssistance?.get('message')?.value;
    const destination = this.formAssistance?.get('destination')?.value;
    const travelCodeIndex = this.codeCountry.indexOf(destination);
    const description = travelCodeIndex !== -1 ? this.descriptionCountry[travelCodeIndex] : '';
    this.travelNation = {
      code: destination,
      description: description,
    };
    const phoneNumber = this.formAssistance?.get('telephone')?.value;
    const isTravelling = this.formAssistance?.get('travel')?.value;
    const individualId = userData?.individualId;
    this.dataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    const travelStartDate = this.formAssistance?.get('departure')?.value;
    const travelEndDate = this.formAssistance?.get('return')?.value;
    const body = {
      currentContactId: individualId,
      travelStartDate: travelStartDate ? travelStartDate.toLocaleDateString('it-IT') : '',
      travelEndDate: travelEndDate ? travelEndDate.toLocaleDateString('it-IT') : '',
      travelPlace: this.travelNation.description,
      travelNation: this.travelNation,
      isTravelling: isTravelling,
      message: message,
      phoneNumber: phoneNumber,
    };
    this.http.callMeBackTravel(body).subscribe({
      next: (response) => {
        this.toastr.success(this.translate.instant('toastr.success.assistanceSuccess'));
        this.router.navigate(['/assistance-service']);
      },
      error: (error) => {
        console.error('Error', error);
      }
    });
  }

  private emitValueForm(): void {
    this.formAssistance?.valueChanges.subscribe(() => {
      const destination = this.formAssistance?.get('destination')?.value;
      const travelCodeIndex = this.codeCountry.indexOf(destination);
      const description = travelCodeIndex !== -1 ? this.descriptionCountry[travelCodeIndex] : '';
      this.travelNation = {
        code: destination,
        description: description,
      };
      this.assistanceFormEmitter.emit({ form: this.formAssistance, destination: this.travelNation?.description });
    });
  }

  ngOnDestroy(): void {
    this.subscription.forEach((subscription: Subscription | undefined) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  public checkDisabledDateEnd = (value: Date): boolean => {
    if (this.formAssistance?.get('departure')?.value) {
      return value.getTime() <= this.formAssistance?.get('departure')?.value.getTime();
    }
    return false;
  };

  public checkDisabledDateStart = (value: Date): boolean => {
    if (this.formAssistance?.get('return')?.value) {
      return value.getTime() >= this.formAssistance?.get('return')?.value.getTime();
    }
    return false;
  };
}
